import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, ShieldAlert } from 'lucide-react';
import { EbError, ebCall, restoreSession, restoreWorkspace } from './api';
import { CloudGate } from './components/CloudGate';
import { SiteAccessGate } from './components/SiteAccessGate';
import { Waiting, Workspace } from './components/Workspace';
import { MusicPlayer } from './components/MusicPlayer';
import { siteAccessStatus } from './siteAccess';
import type { EbSession } from './types';

type BootState = 'booting' | 'signed-out' | 'ready' | 'waiting' | 'blocked' | 'offline';
type SiteAccessState = 'checking' | 'locked' | 'unlocked';

export default function App() {
  const [siteAccess, setSiteAccess] = useState<SiteAccessState>('checking');
  const [state, setState] = useState<BootState>('booting');
  const [session, setSession] = useState<EbSession | null>(null);
  const [message, setMessage] = useState('Restoring your secure Executive Board workspace…');

  const acceptSession = useCallback((next: EbSession) => {
    setSession(next);
    if (next.status === 'SUSPENDED' || next.status === 'REVOKED') {
      setState('blocked');
      setMessage('This Executive Board identity is not currently active. Contact the Secretariat.');
      return;
    }
    setState(next.assignment ? 'ready' : 'waiting');
  }, []);

  const restore = useCallback(async () => {
    setState('booting');
    setMessage('Restoring your secure Executive Board workspace…');
    try {
      try { acceptSession((await restoreWorkspace()).session); }
      catch (problem) {
        if (!(problem instanceof EbError) || !['BACKEND_UNAVAILABLE', 'VALIDATION'].includes(problem.code)) throw problem;
        acceptSession(await restoreSession());
      }
    } catch (problem) {
      if (problem instanceof EbError && problem.code === 'SESSION_EXPIRED') {
        setState('signed-out');
        return;
      }
      setMessage(navigator.onLine
        ? 'The secure EB service could not be reached. No data was changed.'
        : 'You appear to be offline. Reconnect to restore your committee workspace.');
      setState('offline');
    }
  }, [acceptSession]);

  useEffect(() => {
    let active = true;
    void siteAccessStatus()
      .then((unlocked) => { if (active) setSiteAccess(unlocked ? 'unlocked' : 'locked'); })
      .catch(() => { if (active) setSiteAccess('locked'); });
    return () => { active = false; };
  }, []);
  useEffect(() => { if (siteAccess === 'unlocked') void restore(); }, [restore, siteAccess]);

  const logout = useCallback(async () => {
    try { await ebCall('logout'); } catch { /* The cookie still expires server-side. */ }
    setSession(null);
    setState('signed-out');
  }, []);

  if (siteAccess === 'checking') return <BootScreen message="Checking secure website access…" />;
  if (siteAccess === 'locked') return <SiteAccessGate onUnlock={() => setSiteAccess('unlocked')} />;
  if (state === 'booting') return <BootScreen message={message} />;
  if (state === 'offline' || state === 'blocked') {
    return <><main className="status-screen"><section className="status-card"><img src="/brand/crest-384.png" alt="DISMUN" /><ShieldAlert /><p className="eyebrow">EXECUTIVE BOARD ACCESS</p><h1>{state === 'offline' ? 'Unable to connect' : 'Access unavailable'}</h1><p>{message}</p><button className="gold-button" onClick={() => void restore()}><RefreshCw /> Retry securely</button>{session && <button className="secondary-button" onClick={() => void logout()}>Log out</button>}</section></main><MusicPlayer /></>;
  }
  if (state === 'signed-out' || !session) return <><CloudGate onEnter={acceptSession} /><MusicPlayer /></>;
  if (state === 'waiting') return <><Waiting session={session} refresh={restore} logout={logout} /><MusicPlayer /></>;
  return <Workspace key={`${session.eb_identity_id}:${session.assignment?.id}`} initialSession={session} onSession={acceptSession} onLogout={() => { setSession(null); setState('signed-out'); }} />;
}

function BootScreen({ message }: { message: string }) {
  return <main className="boot-screen"><div className="boot-mark"><i /><i /><img src="/brand/crest-384.png" alt="DISMUN 2026" /></div><p>{message}</p></main>;
}
